import { inject, Injectable, Injector } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { every } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { getLocationForFrameNumber } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { getShapeForEventTracking } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { ensureKeyLocation } from '../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import {
  EventTrackingValueWithSource,
  selectionToArray,
} from '../../../cross-team-shared/misc-utils';
import { ObjectWith } from '../../../cross-team-shared/types/misc';
import { outputAnnotationInfoStoreToken } from '../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { VisibilityState } from '../../../team-apis/otterdam/answers/shape-typings';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { OutputKeyboardShortcutService } from '../../outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { PushAndTrackService } from '../../outputs/annotation/annotation-shared/utils/push-and-track.service';
import { ShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/shape-selection.service';
import { contentConnectorToken } from '../../outputs/annotation/utils/misc-utils';
import {
  getMlExtrapolationEventTrackingValues,
  updateExtrapolationsForShapes,
} from '../utils/extrapolation-utils';
import { ShapeAccessService } from './shape-access.service';
import { ShapeService } from './shape.service';

@Injectable()
export class ShapeVisibilityService extends InjectableSuperclass {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private shapeService = inject(ShapeService);
  private fusionManager = inject(FusionManager);

  constructor(
    private keyboardShortcutService: OutputKeyboardShortcutService,
    private store: AbstractAnnotationStore,
    private pushAndTrackService: PushAndTrackService,
    private shapeAccessService: ShapeAccessService,
    private selectionService: ShapeSelectionService,
    private injector: Injector,
  ) {
    super();
  }

  startListening() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('changeShapeVisibility'),
      ({ eventTrackingValue }) =>
        this.changeShapeVisibility(
          eventTrackingValue,
          selectionToArray(this.selectionService.getSelectionForOutput()),
        ),
    );
  }

  changeShapeVisibility(
    eventTrackingValue: EventTrackingValueWithSource,
    shapeIndices: number[],
    newVisibility?: VisibilityState,
  ) {
    const filteredShapeIndices = shapeIndices.filter((index) =>
      this.shapeAccessService.isShapeEditable(index),
    );
    if (filteredShapeIndices.length === 0) {
      return;
    }

    const visibility =
      newVisibility ?? this.calculateNewVisibility(filteredShapeIndices);
    const validShapesForMLExtrapolation = this.getValidShapesForMLExtrapolation(
      visibility,
      filteredShapeIndices,
    );
    this.changeAllShapeVisibilities(visibility, filteredShapeIndices);
    const extrapolatedFrameCounts = updateExtrapolationsForShapes(
      validShapesForMLExtrapolation,
      this.getContentConnector.bind(this),
      this.store.state().frameNumber,
    );
    this.trackEditShapeVisibilityEvent(
      eventTrackingValue,
      visibility,
      filteredShapeIndices,
      extrapolatedFrameCounts,
    );
  }

  private allShapesHaveSameVisibility(
    shapeIndicesToToggleVisibility: number[],
  ) {
    return every(
      shapeIndicesToToggleVisibility,
      (shape) =>
        this.readShapeVisibility(shape) ===
        this.readShapeVisibility(shapeIndicesToToggleVisibility[0]),
    );
  }

  private getKeyLocationStore(shapeIndex: number) {
    const shapeStore = this.getShapeStore(shapeIndex);

    return ensureKeyLocation(
      // @ts-ignore -- fix me
      shapeStore,
      this.store.state().frameNumber,
      this.getContentConnector(shapeIndex),
    );
  }

  private readShapeVisibility(shapeIndex: number) {
    return this.getContentConnector(shapeIndex).determineLocation(
      this.store.state().frameNumber,
      shapeIndex,
    ).visibility;
  }

  private changeAllShapeVisibilities(
    newVisibility: VisibilityState,
    selectedShapeIndices: number[],
  ) {
    selectedShapeIndices.forEach((shapeIndex) => {
      const keyLocationStore = this.getKeyLocationStore(shapeIndex);

      keyLocationStore.getRootStore().batch(() => {
        if (!newVisibility) {
          keyLocationStore('extrapolation').delete();
        }

        keyLocationStore('visibility').set(newVisibility);
      });
    });
  }

  private getValidShapesForMLExtrapolation(
    newVisibility: VisibilityState,
    selectedShapeIndices: number[],
  ) {
    if (!newVisibility) {
      return [];
    }

    return selectedShapeIndices.filter((shapeIndex) => {
      const shapeStore = this.getShapeStore(shapeIndex);

      const keyLocation = getLocationForFrameNumber(
        // @ts-ignore -- fix me
        shapeStore.state().key_locations,
        this.store.state().frameNumber,
      );

      const hasInFlightRequest = this.store
        .state()
        .inFlightMlExtrapolationRequests.some(
          (inFlightRequest) =>
            inFlightRequest.shapeId === shapeIndex &&
            inFlightRequest.keyFrameNumber === this.store.state().frameNumber,
        );

      return !hasInFlightRequest && !keyLocation?.extrapolation;
    });
  }

  private trackEditShapeVisibilityEvent(
    eventTrackingValue: EventTrackingValueWithSource,
    visibility: VisibilityState,
    selectedShapeIndices: number[],
    requestedExtrapolatedFramesCounts: ObjectWith<number | undefined>,
  ) {
    let shapes = selectedShapeIndices.map((shapeIndex) =>
      // @ts-ignore -- fix me
      getShapeForEventTracking(this.shapesStore(shapeIndex).state()),
    );

    shapes = getMlExtrapolationEventTrackingValues(
      requestedExtrapolatedFramesCounts,
      shapes,
    );

    this.pushAndTrackService.pushAndTrack({
      id: 'edit_shape_visibility',
      bucket: 'adjustment',
      ...eventTrackingValue,
      visibility,
      shapes,
      shapes_edited_count: selectedShapeIndices.length,
    });
  }

  private calculateNewVisibility(shapeIndicesToToggleVisibility: number[]) {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return this.calculateNewVisibilityObjectless(
        shapeIndicesToToggleVisibility,
      );
    }
    if (!this.allShapesHaveSameVisibility(shapeIndicesToToggleVisibility)) {
      return 1;
    }

    const oldFirstShapeVisibility = this.readShapeVisibility(
      shapeIndicesToToggleVisibility[0],
    );

    return oldFirstShapeVisibility === 1 ? 0 : 1;
  }

  private calculateNewVisibilityObjectless(
    shapeIndicesToToggleVisibility: number[],
  ) {
    if (!this.allShapesHaveSameVisibility(shapeIndicesToToggleVisibility)) {
      return 1;
    }

    const oldFirstShapeVisibility = this.readShapeVisibility(
      shapeIndicesToToggleVisibility[0],
    );

    return ((oldFirstShapeVisibility + 1) % 1.5) as VisibilityState;
  }

  private getContentConnector(shapeIndex?: number) {
    if (this.taskWorkspaceStore.isUsingObjects() && isDefined(shapeIndex)) {
      const [shape] = this.shapeService.getShapes([shapeIndex]);
      return this.fusionManager.getContentConnector(shape.outputName!);
    }

    return this.injector.get(contentConnectorToken);
  }

  private getShapeStore(shapeIndex: number) {
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return this.shapeService.getShapeStore(shapeIndex);
    } else {
      return this.shapesStore(shapeIndex);
    }
  }
}
